import { useState } from 'react';
import { css } from '@emotion/react';
import { RegisterTerminalStatusResponse_Status } from '@weave/schema-gen-ts/dist/schemas/payments-platform/justifi-integration/justifi_integration.pb';
import { JustfiIntegration1 } from '@frontend/api-justifi-integration';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { BannerNotification, Button, ContentLoader, SkeletonLoader, Text, useAlert } from '@frontend/design-system';
import { RegisterTerminalModalJustifiSteps } from '../hooks';
import {
  RegisterTerminalModalStepProps,
  RegisterTerminalStep,
  RegisterTerminalStepModalWrapper,
} from '../register-terminal-modal-step';

type GenerateDeviceIdProps = RegisterTerminalModalStepProps<{
  onTerminalPaired: () => void;
  onSuccess: () => void;
  locationId: string | undefined;
}>;
export const GenrateDeviceId = ({ locationId, onSuccess, onTerminalPaired, ...props }: GenerateDeviceIdProps) => {
  const { t } = useTranslation('payments');
  const alert = useAlert();
  const [dId, setDId] = useState<string | null>(null);
  const [showStatus, setShowStatus] = useState<boolean>(false);

  const { mutate: registerTerminal, isLoading: genratingDID } =
    JustfiIntegration1.Mutations.useRegisterTerminalMutation({
      options: {
        onSuccess: (data) => {
          setDId(data?.deviceId || null);
        },
        onError: (error: { code?: string; message?: string }) => {
          console.error('Error generating DID', error);
          alert.error(t('There was a problem genrating DID. Please try again later.'));
        },
      },
    });

  const handleDidGenration = () => {
    registerTerminal({
      locationId: locationId || '',
    });
  };

  const { data, isLoading } = JustfiIntegration1.Queries.useRegisterTerminalStatusQuery({
    request: {
      locationId: locationId || '',
      deviceId: dId || '',
    },
    options: {
      enabled: !!locationId && !!dId && showStatus,
    },
  });

  const error = data?.status
    ? data?.status !== RegisterTerminalStatusResponse_Status.TERMINAL_REGISTERED_STATUS_SUCCEEDED
    : false;
  const showStatusScreen = () => setShowStatus(true);
  const resetStatus = () => {
    setDId(null);
    setShowStatus(false);
  };

  const action = showStatus
    ? error
      ? RegisterTerminalStep.TryAgain
      : RegisterTerminalStep.Complete
    : RegisterTerminalStep.Register;
  const actionHandler = showStatus ? (error ? resetStatus : onSuccess) : showStatusScreen;

  return (
    <RegisterTerminalStepModalWrapper
      title={t('Generate a Device ID (DID)')}
      nextStep={RegisterTerminalModalJustifiSteps.GenrateDeviceId}
      onClickAdvance={actionHandler}
      Action={action}
      isAdvanceDisabled={!dId}
      backLabel={showStatus && !error ? t('Register Another Terminal') : undefined}
      {...props}
      goBack={showStatus && !error ? resetStatus : props.goBack}
    >
      {!showStatus ? (
        <GenerateDIDContent genrateDId={handleDidGenration} dId={dId} isLoading={genratingDID} />
      ) : (
        <StatusMessage error={error} isLoading={isLoading} />
      )}
    </RegisterTerminalStepModalWrapper>
  );
};

type GenerateDIDContentProps = {
  genrateDId: () => void;
  dId: string | null;
  isLoading: boolean;
};
const GenerateDIDContent = ({ genrateDId, dId, isLoading }: GenerateDIDContentProps) => {
  const { t } = useTranslation('payments');

  return (
    <>
      <Text>{t('Select "Generate" below for your 8-digit DID number.')}</Text>
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1),
        }}
      >
        <Button
          variant='tertiary'
          onClick={genrateDId}
          disabled={isLoading || !!dId}
          css={css`
            width: fit-content;
            font-weight: ${theme.font.weight.bold};
          `}
        >
          {t('Generate DID')}
        </Button>
        {isLoading && <SkeletonLoader width='100px' isText />}
        {dId && <Text weight='bold'>{dId}</Text>}
      </div>
      <Text
        css={css`
          visibility: ${dId ? 'visible' : 'hidden'};
        `}
      >
        {t(
          'Input the number above into your terminal on the Device ID screen. Press "Next" when you have inputted the DID.'
        )}
      </Text>
    </>
  );
};

type StatusMessageProps = {
  isLoading: boolean;
  error?: boolean;
};
const StatusMessage = ({ isLoading, error }: StatusMessageProps) => {
  const { t } = useTranslation('payments');

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(2)};
        margin: ${theme.spacing(2, 0)};
      `}
    >
      {isLoading ? (
        <div
          css={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '150px',
          }}
        >
          <ContentLoader show />
        </div>
      ) : (
        <>
          <BannerNotification
            css={css`
              margin-top: ${theme.spacing(2)};
              max-width: fit-content;
            `}
            status={error ? 'error' : 'success'}
            message={error ? t('There was a problem registering your terminal') : t('Terminal connected!')}
          />
          {!error && <Text weight='bold'>{t('You are now ready to collect payments!')}</Text>}
        </>
      )}
    </div>
  );
};
